export const endpoints = {
  auth: {
    // Backend-mediated login (DEC-AUTH-001 / FEAT-070): the FE posts {email,password} here; the BE
    // Backend-mediated login: the FE posts {email,password} here; the BE
    // verifies via Firebase, applies rate-limit / 5-fail lockout / single-session, and returns a Firebase
    // CUSTOM token the FE exchanges via signInWithCustomToken (per-request ID tokens carry the freshly-
    // bumped session_version claim). The FE NEVER calls signInWithEmailAndPassword anymore.
    login: '/admin/auth/login',
    me: '/admin/me',
    passwordResetSendCode: '/auth/password-reset/send-code',
    passwordResetVerifyCode: '/auth/password-reset/verify-code',
    passwordResetChange: '/auth/password-reset/change',
    // PRD 20 — My Profile.
    mePhoneChangeSendCode: '/auth/me/phone-change/send-code',
    mePhoneChangeVerifyCode: '/auth/me/phone-change/verify-code',
    mePhoneChange: '/auth/me/phone-change',
    mePasswordChange: '/auth/me/password-change',
    // Reload session re-establishment (FEATURE_005): on a hard reload the FE POSTs here with the HttpOnly
    // refresh cookie + a custom CSRF header; the BE rotates the cookie and returns a fresh custom token.
    // logout drops the server-side token + clears the cookie so a later reload can't re-establish.
    refresh: '/admin/auth/refresh',
    logout: '/admin/auth/logout',
    // Invite-prefill lookup — built 2026-06-09 (token-gated, public); returns {accountType,email,name,institution,roles,...}.
    signupVerify: '/admin/auth/signup/invitation',
    // US-SU-05 — token-gated (public) list of PUBLISHED Terms shown in the sign-up consent section.
    signupTerms: '/admin/auth/signup/terms',
    // FEATURE_041 — fully public (no token) list of PUBLISHED Terms shown by the login/signup
    // footer legal links before a session exists. BE: GET /api/v1/admin/auth/terms (permitAll,
    // commit 05f7678). Distinct from signupTerms, which requires an invitation token.
    publicTerms: '/admin/auth/terms',
    signupSendCode: '/admin/auth/signup/request-otp',
    signupVerifyCode: '/admin/auth/signup/verify-otp',
    signup: '/admin/auth/signup',
    // Password-reset OTP flow. request-otp is anti-enumeration (always 204, same whether or not the
    // email is registered). verify-otp returns { resetToken }. complete finishes the forgot-password
    // (resetToken) flow; the forced post-login change posts to the dedicated forced-change endpoint.
    passwordResetRequestOtp: '/admin/auth/password-reset/request-otp',
    passwordResetVerifyOtp: '/admin/auth/password-reset/verify-otp',
    passwordResetComplete: '/admin/auth/password-reset/complete',
    passwordResetForcedChange: '/admin/auth/password-reset/forced-change',
    // PRD 20 — My Profile (FEATURE_052/053). Backend: kr.co.ards.admin.profile.MyProfileController.
    // 2-endpoint phone flow — request-otp sends the SMS; verify checks the code AND commits the new
    // number (the prototype's separate 저장 button triggers this verify-commit). All self-scoped.
    myProfile: '/admin/my-profile',
    myProfilePhoneRequestOtp: '/admin/my-profile/phone/request-otp',
    myProfilePhoneVerify: '/admin/my-profile/phone/verify',
    myProfilePassword: '/admin/my-profile/password',
  },
  // Paths are relative to VITE_API_BASE_URL (= http://localhost:9291/api/v1).
  // ALL admin-web endpoints live under the BE base /api/v1/admin
  roles: '/admin/roles',
  admins: '/admin/admins',
  // Backend-owned UI-label catalog (the FE fetches it and maps a wire enum code to its Korean label).
  i18n: '/admin/i18n',
  // Server-rendered email previews (the FE displays the BE's single-owner render).
  emailPreviews: '/admin/email-previews',
  medicalStaff: '/admin/medical-staff',
  diseaseGroups: '/admin/disease-groups',
  diseaseCodes: '/admin/disease-codes',
  terms: '/admin/terms',
  users: '/users',
  // BE: kr.co.ards.admin.patient.PatientController @ /api/v1/admin/patients (list built; detail/register/
  // close/export pending). Derived paths (/export, /{id}, /{id}/close) hang off this base.
  patients: '/admin/patients',
  // PRD 18 — Log Inquiry (FEATURE_050). Admin-portal-scoped: the page is the
  // SSoT for admin + medical-staff dashboard activity logs (PRD 18 §1.1).
  // Real BE: kr.co.ards.admin audit-log controller @ /api/v1/admin/audit-logs
  // (cursor-paged, newest-first, no server-side sort).
  logs: '/admin/audit-logs',
  // CSV export of the audit log (FEATURE_062). Streamed text/csv, server-rendered
  // Korean labels, SYSTEM_MGMT:WRITE / Root. kr.co.ards.admin.export.CsvExportController.
  logsExport: '/admin/audit-logs/export',
  inquiries: '/medical-inquiries',
  // PRD 13 — Notice Management (공지사항 관리). FE proposal; BE not confirmed.
  // See docs/api-contracts/notice-management.md.
  notices: '/admin/notices',
  // PRD 12 — Patient Inquiry Management. FE proposal (BE unconfirmed): admin-scoped
  // CRUD on patient inquiries + reply thread. Derived paths (/{id}, /{id}/reply)
  // hang off this base. See docs/api-contracts/patient-inquiry-management.md.
  patientInquiries: '/admin/patient-inquiries',
  schedules: '/schedules',
  notifications: '/notifications',
  // PRD 17 — Notification Policy (알림 정책, FEATURE_049). Fixed 6-event catalog.
  // BE: kr.co.ards.admin.notificationpolicy.NotificationPolicyController (read-only viewer, no write path).
  // GET `/admin/notification-policies` lists every catalog event.
  notificationPolicies: '/admin/notification-policies',
  // GET `.../sending-reference` — SMS·email inventory shown at the page bottom (PRD 17 §3.8).
  notificationSendingReference: '/admin/notification-policies/sending-reference',
   // PRD 07 — Management Guidelines (FEATURE_030). Real BE: kr.co.ards.admin.guideline.GuidelineController
   // @ /api/v1/admin/management-guidelines (path id = the display code G-XXX). List/create/get/update(PATCH)/delete.
  managementGuidelines: '/admin/management-guidelines',
  // FEATURE_061 block-media upload (image/video) → GCS. Multipart POST; returns { url, objectKey }.
  // Real BE: kr.co.ards.admin.common.storage.UploadController @ /api/v1/admin/management-guidelines/uploads.
  uploads: '/admin/management-guidelines/uploads',
   // PRD 07 — Management Guidelines.
  // PRD 11 — Counseling Manual (상담매뉴얼). Category CRUD hangs off
  // `${consultationManuals}/categories` (this feature owns its own tree copy).
  consultationManuals: '/admin/consultation-manuals',
  // Used by Management Guidelines for the cascading Area/Sub-area/Item select
  // and the read-only PID chips. Will be sourced from the PRO Item Design
  // feature once that page is implemented in React.
  proTreeTopics: '/pro-tree/topics',
  proItems: '/pro-items',
  // Read-only whole PRO tree (영역→하위영역→항목) shared by Questionnaire Design
  // (PRD 05) via the pro-tree feature. Owned by PRO Item Design (PRD 04) long-term.
  proTree: '/admin/pro/tree',
  biometricRanges: '/admin/biometric-ranges',
  // PRD 09 §3.4 — R-value (위험지수) 3-interval threshold config. One GET/PUT for all three bands.
  // The server names them bands (LOW/MID/HIGH) and replies with a list; the adapter folds that back
  // into the card's { low, mid, high } shape.
  riskBands: '/admin/biometric-risk-bands',
  // PRD 04 — PRO Item Design. See docs/api-contracts/pro-item-design.md.
  proItemDesign: {
    tree: '/admin/pro-items/tree',
    items: '/admin/pro-items/items',
    domains: '/admin/pro-items/domains',
    domainSubAreas: (domainId: number) => `/admin/pro-items/domains/${domainId}/sub-areas`,
    subAreas: '/admin/pro-items/sub-areas',
    subAreaItems: (subAreaId: number) => `/admin/pro-items/sub-areas/${subAreaId}/items`,
    itemPublish: (itemId: number) => `/admin/pro-items/items/${itemId}/publish`,
    reorder: '/admin/pro-items/reorder',
  },
  questionnaireDesignItems: '/admin/questionnaire-design/items',
  questionnaireDesignQuestions: '/admin/questionnaire-design/questions',
  // PRD 10 — Health Encyclopedia. See docs/api-contracts/health-encyclopedia.md.
  // Sub-resources: `${healthEncyclopedia}/contents`, `/categories`, `/uploads`.
  healthEncyclopedia: '/admin/health-encyclopedia',
  // PRD 06 — PRO Deployment Management. See docs/api-contracts/pro-deployment-management.md.
  proDeployment: '/admin/pro-deployment',
  // PRD 01 — Home Dashboard (FEATURE_010). Read-only aggregates + charts.
  // BE: kr.co.ards.admin.dashboard.DashboardController @ /api/v1/admin/dashboard.
  // `stats` = the 5 summary cards; `charts?period=weekly|monthly` = the 2 time-series charts.
  dashboard: {
    stats: '/admin/dashboard/stats',
    charts: '/admin/dashboard/charts',
  },
  // Organization details for the auth-page footers. Fully public (no token): the footer renders on
  // login / signup / password-reset, all before a session exists. BE: PublicInstitutionController @
  // GET /api/v1/admin/institution (permitAll). Replaces the three literals the `auth` catalog used to
  // carry, so the footer can no longer disagree with the rest of the product about who runs the service.
  institution: {
    public: '/admin/institution',
  },
};
